// Channel × Vertical × Platform 联动规则 / Channel × Vertical × Platform coupling.
// 按 skills/pmf-validation 领域画像归纳：
//   - Channel 是主约束（决定传播机制）；Vertical 与 Platform 的合法集合由 Channel 派生。
//   - Vertical × Platform 本身基本无硬冲突，所以不引入二阶约束。
//   - generic 通道接受所有 Vertical 与 Platform。
// 参考：
//   skills/pmf-validation/channels/*.md 的"代表平台"与"典型场景"段落。
//   skills/pmf-validation/verticals/*.md 的"适用于 …"段落。
// Derived from skills/pmf-validation domain profiles: Channel drives which
// Verticals and Platforms are semantically valid; Vertical × Platform has
// no hard conflicts, so we only implement the one-way coupling.

import type { ConfigOption } from "@/lib/config-labels";
import { getConfigOptions } from "@/lib/config-labels";

// 按渠道语义圈定的兼容 vertical 集合。undefined 表示不限制。
// Vertical allow-list per channel. undefined means no restriction.
const CHANNEL_VERTICAL_WHITELIST: Record<string, ReadonlyArray<string>> = {
  "algorithm-ecommerce": [
    "fmcg",
    "beauty-personal-care",
    "fashion-retail",
    "consumer-electronics",
    "consumer-ai-hardware",
  ],
  "search-ecommerce": [
    "consumer-electronics",
    "consumer-ai-hardware",
    "beauty-personal-care",
    "fmcg",
    "fashion-retail",
  ],
  "social-ecommerce": [
    "fmcg",
    "beauty-personal-care",
    "fashion-retail",
    "consumer-electronics",
    "consumer-ai-hardware",
    "mobile-app",
  ],
  "content-seeding": [
    "fmcg",
    "beauty-personal-care",
    "fashion-retail",
    "consumer-electronics",
    "consumer-ai-hardware",
    "mobile-app",
    "saas",
  ],
  "offline-experience": [
    "fashion-retail",
    "beauty-personal-care",
    "consumer-electronics",
    "consumer-ai-hardware",
    "fmcg",
  ],
  "offline-distribution": ["fmcg", "beauty-personal-care", "fashion-retail"],
  "enterprise-sales": ["saas", "consumer-electronics", "consumer-ai-hardware"],
  "app-distribution": ["mobile-app"],
  // generic → 全部保留（无 whitelist 条目）
  // generic falls through to "no restriction".
};

// 按渠道语义圈定的兼容 platform 集合。空字符串代表"未设置专属平台"，永远保留。
// Platform allow-list per channel. Empty string "未设置专属平台" is always kept.
const CHANNEL_PLATFORM_WHITELIST: Record<string, ReadonlyArray<string>> = {
  "algorithm-ecommerce": ["", "douyin", "generic"],
  "search-ecommerce": ["", "xiaohongshu", "generic"],
  "social-ecommerce": ["", "douyin", "tiktok-shop-id", "tiktok-shop-sea", "generic"],
  "content-seeding": [
    "",
    "xiaohongshu",
    "douyin",
    "weibo",
    "tiktok-shop-id",
    "tiktok-shop-sea",
    "generic",
  ],
  "offline-experience": ["", "generic"],
  "offline-distribution": ["", "generic"],
  "enterprise-sales": ["", "generic"],
  "app-distribution": ["", "douyin", "generic"],
  // generic → 全部保留
  // generic falls through.
};

/**
 * 根据当前 channel 过滤 vertical 选项 / Filter vertical options for the given channel.
 * 未被白名单包含的选项会被剔除；channel 自身未知时不做限制。
 * Returns all vertical options when the channel is unknown or unconstrained.
 */
export function verticalOptionsForChannel(channel: string): ConfigOption[] {
  const whitelist = CHANNEL_VERTICAL_WHITELIST[channel];
  const all = getConfigOptions("vertical");
  if (!whitelist) return all;
  return optionsInWhitelistOrder(all, whitelist);
}

/**
 * 根据当前 channel 过滤 platform 选项 / Filter platform options for the given channel.
 */
export function platformOptionsForChannel(channel: string): ConfigOption[] {
  const whitelist = CHANNEL_PLATFORM_WHITELIST[channel];
  const all = getConfigOptions("platform");
  if (!whitelist) return all;
  return optionsInWhitelistOrder(all, whitelist);
}

/**
 * 判断当前 vertical 是否与 channel 兼容 / Whether a vertical pairs with the channel.
 */
export function isVerticalCompatible(channel: string, vertical: string): boolean {
  const whitelist = CHANNEL_VERTICAL_WHITELIST[channel];
  if (!whitelist) return true;
  return whitelist.includes(vertical);
}

/**
 * 判断当前 platform 是否与 channel 兼容 / Whether a platform pairs with the channel.
 */
export function isPlatformCompatible(channel: string, platform: string): boolean {
  const whitelist = CHANNEL_PLATFORM_WHITELIST[channel];
  if (!whitelist) return true;
  return whitelist.includes(platform);
}

/**
 * 当 channel 变动、而旧 vertical 与新 channel 不兼容时给出默认 vertical。
 * Pick a default vertical when the current one is incompatible with the new channel.
 */
export function defaultVerticalForChannel(channel: string): string {
  const options = verticalOptionsForChannel(channel);
  return options[0]?.value ?? "";
}

/**
 * 当 channel 变动、而旧 platform 与新 channel 不兼容时给出默认 platform。
 * Pick a compatible default platform when the current value no longer fits.
 */
export function defaultPlatformForChannel(channel: string): string {
  const options = platformOptionsForChannel(channel);
  // Task 详情沿用历史行为：存在 generic 时优先使用"通用"平台。
  // Task Detail keeps legacy behavior: prefer the generic platform when available.
  if (options.some((option) => option.value === "generic")) return "generic";
  return options[0]?.value ?? "";
}

// 保留 whitelist 的业务优先级顺序 / Preserve the business priority order from the allow-list.
function optionsInWhitelistOrder(
  all: ConfigOption[],
  whitelist: ReadonlyArray<string>,
): ConfigOption[] {
  const byValue = new Map(all.map((option) => [option.value, option]));
  return whitelist.flatMap((value) => {
    const option = byValue.get(value);
    return option ? [option] : [];
  });
}
